import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr, enUS, it } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

interface Appointment {
  id: number;
  appointment_date: string;
  appointment_time: string;
  status: 'pending' | 'confirmed' | 'cancelled';
  user_id: string;
}

interface RendezVousModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
  language: string;
}

const RendezVousModal: React.FC<RendezVousModalProps> = ({ open, onOpenChange, user, language }) => {
  const { toast } = useToast();
  const { tl } = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const dateLocale = language === 'fr' ? fr : language === 'it' ? it : enUS;

  // Fetch existing appointments
  const { data: appointments = [], isLoading: isLoadingAppointments } = useQuery({
    queryKey: ['appointments'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rendezvous')
        .select('appointment_date, appointment_time, status, user_id')
        .order('appointment_date', { ascending: true });
      if (error) throw error;
      return data as Appointment[];
    }
  });

  // Check if user has an appointment in the same week
  const hasAppointmentInSameWeek = (date: Date) => {
    if (!user || !appointments) return false;
    const selectedDate = new Date(date);
    const selectedWeekStart = new Date(selectedDate);
    selectedWeekStart.setDate(selectedDate.getDate() - selectedDate.getDay());
    const selectedWeekEnd = new Date(selectedWeekStart);
    selectedWeekEnd.setDate(selectedWeekStart.getDate() + 6);
    return appointments.some(appointment => {
      if (appointment.user_id !== user.id || appointment.status === 'cancelled') return false;
      const appointmentDate = new Date(appointment.appointment_date);
      return appointmentDate >= selectedWeekStart && appointmentDate <= selectedWeekEnd;
    });
  };

  // Generate time slots (9:00 to 18:00 with 30-minute intervals)
  const timeSlots = Array.from({ length: 19 }, (_, i) => {
    const hour = 9 + Math.floor(i / 2);
    const minute = i % 2 === 0 ? '00' : '30';
    return `${hour.toString().padStart(2, '0')}:${minute}`;
  });

  // Check if a time slot is available
  const isTimeSlotAvailable = (date: Date, time: string) => {
    if (!appointments || appointments.length === 0) return true;
    const selectedDateStr = format(date, 'yyyy-MM-dd');
    return !appointments.some(appointment => {
      const isSameDate = appointment.appointment_date === selectedDateStr;
      const isSameTime = appointment.appointment_time === time || appointment.appointment_time === `${time}:00`;
      const isNotCancelled = appointment.status !== 'cancelled';
      return isSameDate && isSameTime && isNotCancelled;
    });
  };

  // Handle appointment confirmation
  const handleAppointmentConfirmation = async () => {
    if (!selectedDate || !selectedTime || !user) return;
    if (hasAppointmentInSameWeek(selectedDate)) {
      toast({
        title: tl('Rendez-vous non disponible', 'Appointment unavailable', 'Appuntamento non disponibile'),
        description: tl(
          'Vous avez déjà un rendez-vous prévu cette semaine. Veuillez choisir une autre semaine.',
          'You already have an appointment scheduled this week. Please choose another week.',
          "Avete già un appuntamento previsto questa settimana. Scegliete un'altra settimana."
        ),
        variant: 'destructive',
      });
      return;
    }
    if (!isTimeSlotAvailable(selectedDate, selectedTime)) {
      toast({
        title: tl('Créneau non disponible', 'Slot unavailable', 'Fascia oraria non disponibile'),
        description: tl(
          'Ce créneau a été réservé entre-temps. Veuillez choisir un autre horaire.',
          'This slot was booked in the meantime. Please choose another time.',
          'Questa fascia oraria è stata prenotata nel frattempo. Scegliete un altro orario.'
        ),
        variant: 'destructive',
      });
      return;
    }
    try {
      const { error } = await supabase
        .from('rendezvous')
        .insert([
          {
            user_id: user.id,
            appointment_date: format(selectedDate, 'yyyy-MM-dd'),
            appointment_time: selectedTime,
            status: 'pending'
          }
        ]);
      if (error) throw error;
      toast({
        title: tl('Rendez-vous confirmé', 'Appointment confirmed', 'Appuntamento confermato'),
        description: tl(
          'Votre rendez-vous a été enregistré avec succès.',
          'Your appointment has been successfully booked.',
          'Il vostro appuntamento è stato registrato con successo.'
        ),
      });
      setIsConfirmationOpen(false);
      onOpenChange(false);
      setSelectedDate(undefined);
      setSelectedTime('');
    } catch (error) {
      toast({
        title: tl('Erreur', 'Error', 'Errore'),
        description: tl(
          'Une erreur est survenue lors de la création du rendez-vous.',
          'An error occurred while creating the appointment.',
          "Si è verificato un errore durante la creazione dell'appuntamento."
        ),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px] bg-white text-black max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{tl('Prendre un rendez-vous', 'Book an appointment', 'Prenota un appuntamento')}</DialogTitle>
            <DialogDescription>
              {!user ? (
                <p className="text-red-500">
                  {tl(
                    'Veuillez vous connecter pour prendre un rendez-vous.',
                    'Please sign in to book an appointment.',
                    'Accedete per prenotare un appuntamento.'
                  )}
                </p>
              ) : (
                tl(
                  'Sélectionnez une date et une heure pour votre visite.',
                  'Select a date and time for your visit.',
                  'Selezionate una data e un orario per la vostra visita.'
                )
              )}
            </DialogDescription>
          </DialogHeader>
          {user ? (
            <div className="grid gap-4 py-4">
              <div>
                <h3 className="text-sm font-medium mb-2">
                  {tl('Sélectionnez une date', 'Select a date', 'Selezionate una data')}
                </h3>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today || hasAppointmentInSameWeek(date);
                  }}
                  className="rounded-md border"
                />
                {selectedDate && hasAppointmentInSameWeek(selectedDate) && (
                  <p className="text-red-500 text-sm mt-2">
                    {tl(
                      'Vous avez déjà un rendez-vous prévu cette semaine.',
                      'You already have an appointment scheduled this week.',
                      'Avete già un appuntamento previsto questa settimana.'
                    )}
                  </p>
                )}
              </div>
              {selectedDate && (
                <div>
                  <h3 className="text-sm font-medium mb-2">
                    {tl('Sélectionnez une heure', 'Select a time', 'Selezionate un orario')}
                  </h3>
                  {isLoadingAppointments ? (
                    <div className="flex justify-center items-center h-32">
                      <p className="text-gray-500">
                        {tl(
                          'Chargement des créneaux disponibles...',
                          'Loading available slots...',
                          'Caricamento delle fasce orarie disponibili...'
                        )}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
                      {timeSlots.map((time) => {
                        const isAvailable = isTimeSlotAvailable(selectedDate, time);
                        return (
                          <Button
                            key={time}
                            variant={selectedTime === time ? "default" : "outline"}
                            onClick={() => {
                              if (isAvailable) {
                                setSelectedTime(time);
                              }
                            }}
                            disabled={!isAvailable}
                            className={cn(
                              "h-10 text-sm",
                              !isAvailable && "opacity-50 cursor-not-allowed bg-gray-100"
                            )}
                          >
                            {time}
                            {!isAvailable && (
                              <span className="ml-1 text-xs">
                                ({tl('Occupé', 'Booked', 'Occupato')})
                              </span>
                            )}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="py-4 text-center">
              <Button
                variant="default"
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                {tl('Se connecter', 'Sign in', 'Accedi')}
              </Button>
            </div>
          )}
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="w-full sm:w-auto"
            >
              {tl('Annuler', 'Cancel', 'Annulla')}
            </Button>
            {user && (
              <Button
                onClick={() => setIsConfirmationOpen(true)}
                disabled={!selectedDate || !selectedTime || !isTimeSlotAvailable(selectedDate, selectedTime) || hasAppointmentInSameWeek(selectedDate)}
                variant="luxury"
                className="w-full sm:w-auto"
              >
                {tl('Confirmer', 'Confirm', 'Conferma')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirmation Dialog */}
      <Dialog open={isConfirmationOpen} onOpenChange={setIsConfirmationOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-[425px] bg-white text-black">
          <DialogHeader>
            <DialogTitle>
              {tl('Confirmer le rendez-vous', 'Confirm appointment', "Conferma l'appuntamento")}
            </DialogTitle>
            <DialogDescription>
              {tl(
                'Veuillez vérifier les détails de votre rendez-vous avant de confirmer.',
                'Please check your appointment details before confirming.',
                "Verificate i dettagli dell'appuntamento prima di confermare."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <h3 className="text-sm font-medium">{tl('Date', 'Date', 'Data')}</h3>
              <p className="text-base">
                {selectedDate && format(selectedDate, 'EEEE d MMMM yyyy', { locale: dateLocale })}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium">{tl('Heure', 'Time', 'Ora')}</h3>
              <p className="text-base">{selectedTime}</p>
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmationOpen(false)}
              className="w-full sm:w-auto"
            >
              {tl('Modifier', 'Modify', 'Modifica')}
            </Button>
            <Button
              onClick={handleAppointmentConfirmation}
              className="w-full sm:w-auto"
            >
              {tl('Confirmer le rendez-vous', 'Confirm appointment', "Conferma l'appuntamento")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RendezVousModal;
